<sidebar class="sidebar">
  <timesheet-sidebar
    [columns]="columns"
    [show]="options?.show"
    [status]="options?.statusFilters"
    [type]="options?.typeFilters"
    [customFieldsColumns]="options?.customFields"
  >
  </timesheet-sidebar>
</sidebar>

<form class="mb-md flex items-center justify-end gap-sm" [formGroup]="periodForm">
  <label [for]="periodInputId" class="text-base font-semibold text-default">{{ 'Period' | translate }}</label>
  <sb-date-range-picker [id]="periodInputId" formControlName="period" [maxDate]="today" />
</form>

<stat-summary class="mb-lg block">
  <stat-summary-block>
    <stat-summary-value [loading]="isLoading">
      {{ viewData?.shiftCount }}
    </stat-summary-value>
    <span statSummaryTitle>
      <sb-icon size="lg" [name]="'calendar_approved'" />
      {{ 'Total shifts' | translate }}
    </span>
  </stat-summary-block>
  <stat-summary-block>
    <stat-summary-value [loading]="isLoading">
      {{ viewData?.approvedTotal.hours | localeDuration: 'digital' }}
    </stat-summary-value>
    <span statSummaryTitle>
      <sb-icon size="lg" [name]="'stopwatch'" />
      {{ 'Total hours' | translate }}
    </span>
  </stat-summary-block>
  <ng-container *subscriptionPlan="planType.BASIC">
    @if (viewData?.canViewSalary) {
      <stat-summary-block>
        <stat-summary-value [loading]="isLoading" data-hj-suppress>
          {{ viewData?.approvedTotal.pay | money }}
        </stat-summary-value>
        <span statSummaryTitle>
          <sb-icon size="lg" [name]="'payment'" />
          {{ 'Total salary' | translate }}
        </span>
      </stat-summary-block>
      @if (showCoc) {
        <stat-summary-block>
          <stat-summary-value [loading]="isLoading" data-hj-suppress>
            {{ viewData?.approvedTotal.coc | money }}
          </stat-summary-value>
          <span statSummaryTitle>
            <sb-icon size="lg" [name]="'calculator'" />
            {{ 'Total CTC' | translate }}
          </span>
        </stat-summary-block>
      }
    }
  </ng-container>
</stat-summary>

<div class="mb-md flex flex-wrap items-center justify-between gap-md">
  <h2 class="reset-h text-lg font-semibold text-default">{{ 'Worked hours' | translate }}</h2>
  <div class="flex items-center gap-sm">
    <filters-toggle [isActive]="isAnyTimesheetFilterActive$ | async" (toggleClick)="openSidebar()" />

    @if (canCreateTimesheets()) {
      <a sb-button color="primary" [routerLink]="['', { outlets: { modal: ['timesheet', { employee: employeeId }] } }]">
        <sb-icon name="add-regular" />
        <span class="max-sm:hidden">{{ 'Add worked hours' | translate }}</span>
      </a>
    }
  </div>
</div>
<div class="flex min-h-0 flex-col">
  @if (isLoading) {
    <sb-loading-state class="py-2xl" />
  } @else if (!viewData?.days?.length) {
    <sb-card>
      <sb-empty-state
        class="py-2xl"
        icon="stopwatch"
        [title]="'Nothing found' | translate"
        [description]="'Try adjusting the filters' | translate"
      />
    </sb-card>
  } @else {
    <!--
      A bounded column: the header and totals rows keep their height either side of
      a body that takes what is left of the page and scrolls in both directions, so
      its horizontal scrollbar stays on screen however many days the period holds.
      Sitting outside that scroll container, the header and totals rows follow its
      horizontal offset rather than scrolling themselves.
    -->
    <div class="timesheet flex min-h-0 flex-col">
      <!--
        The followers reserve the body's scrollbar gutter beside their row, so the
        rules separating them from the body are drawn on the followers, where they
        run on under that gutter, rather than on their rows.
      -->
      <div class="shrink-0 overflow-x-hidden border-b border-default bg-surface-page" [sbGridScrollFollower]="gridBody">
        <div sbGridScrollRow class="timesheet-row timesheet-day-header-row border-b-0">
          <timesheet-day-header
            sbGridStickyColumn
            [sbGridStickyColumnAffordance]="false"
            class="timesheet-info-cell"
            [title]="'Date' | translate"
            [showAsFooter]="false"
          >
          </timesheet-day-header>
          <timesheet-content-row class="timesheet-centre-columns">
            <div class="timesheet-day-header timesheet__status timesheet-column--status"></div>
            <div class="timesheet-day-header timesheet-column--start">
              {{ 'Start' | translate }}
            </div>
            <div class="timesheet-day-header timesheet-column--end">{{ 'End' | translate }}</div>
            @if (columns?.break) {
              <div class="timesheet-day-header timesheet-column--break">
                @if (isPaidBreaksEnabled()) {
                  {{ 'Unpaid break' | translate }}
                } @else {
                  {{ 'Break' | translate }}
                }
              </div>
            }
            @if (columns?.paidBreak) {
              <div class="timesheet-day-header timesheet-column--paid-break">
                {{ 'Paid break' | translate }}
              </div>
            }
            @if (columns?.location) {
              <div class="timesheet-day-header timesheet-column--location">
                {{ 'Location' | translate }}
              </div>
            }
            @if (columns?.department) {
              <div class="timesheet-day-header timesheet-column--department">
                {{ 'Department' | translate }}
              </div>
            }
            @if (columns?.team) {
              <div class="timesheet-day-header timesheet-column--team">
                {{ 'Team' | translate }}
              </div>
            }
            @if (columns?.shift) {
              <div class="timesheet-day-header timesheet-column--shift">
                {{ 'Shift' | translate }}
              </div>
            }
            @if (columns?.surcharge) {
              <div class="timesheet-day-header timesheet-column--surcharge">
                {{ 'Rate card' | translate }}
              </div>
            }
            @if (columns?.km) {
              <div class="timesheet-day-header timesheet-column--km">
                {{ 'Kilometers' | translate }}
              </div>
            }
            @if (columns?.meals) {
              <div class="timesheet-day-header timesheet-column--meals">
                {{ 'Meals' | translate }}
              </div>
            }
            @if (columns?.note) {
              <div class="timesheet-day-header timesheet-column--note">
                {{ 'Note' | translate }}
              </div>
            }
            @for (customField of customFields; track customField) {
              @if (options?.customFields && (options?.customFields)[customField.id]) {
                <div class="timesheet-day-header" [ngStyle]="{ 'width.px': customField.width }">
                  {{ customField.name }}&nbsp;
                  @if (!!customField.description) {
                    <sb-icon
                      name="information-regular"
                      [sbTooltipPlacement]="'top'"
                      [sbTooltip]="customField.description"
                    />
                  }
                </div>
              }
            }
            <div class="timesheet-day-header timesheet-column--details">
              {{ 'Details' | translate }}
            </div>
          </timesheet-content-row>
          <div sbGridStickyColumn="right" [sbGridStickyColumnAffordance]="false" class="timesheet-totals-cell">
            <!-- Reserved space for the action buttons -->
            <timesheet-day-header class="timesheet__actions" [title]="" [showAsFooter]="false" />
            <div class="timesheet-header-totals">
              <timesheet-day-header [title]="'Hours' | translate" [showAsFooter]="false" />
              @if (viewData?.canViewSalary) {
                <timesheet-day-header [title]="'Salary' | translate" [showAsFooter]="false" />
              }
              @if (viewData?.canViewSalary && showCoc) {
                <timesheet-day-header [title]="'CTC' | translate" [showAsFooter]="false" />
              }
            </div>
          </div>
        </div>
      </div>
      <div sbGridScrollViewport #gridBody="sbGridScrollViewport" class="min-h-0 overflow-auto">
        @for (day of viewData?.days; track trackByDate($index, day)) {
          @for (row of day.rows; track trackByRow(i, row); let i = $index) {
            <div sbGridScrollRow class="timesheet-row">
              <timesheet-day-info
                sbGridStickyColumn
                [sbGridStickyColumnAffordance]="false"
                class="timesheet-info-cell"
                [date]="day.day"
                [approvedTotal]="day.approvedTotal"
                [showSalary]="viewData.canViewSalary"
                [scheduleDiff]="day.scheduleDiff"
                [show]="i === 0"
                [last]="i === day.rows.length - 1"
              >
              </timesheet-day-info>
              @switch (row.type) {
                @case ('timesheet') {
                  <timesheet-content-row class="timesheet-centre-columns">
                    @if (!row.timesheet.canEdit && !row.timesheet.canApprove) {
                      <timesheet-row-timesheet
                        [timesheet]="row.timesheet"
                        [locations]="options?.locationsById"
                        [department]="(options?.departmentsById)[row.timesheet.department_id]"
                        [rateCards]="options?.rateCards"
                        [absenceWarning]="row.absenceWarning"
                        [doubleRegistration]="row.doubleRegistration"
                        [deviationsFromSchedule]="row.deviationsFromSchedule"
                        [cols]="columns"
                        [customFields]="customFields"
                        [customFieldCols]="options.customFields"
                      >
                      </timesheet-row-timesheet>
                    }
                    @if ((row.timesheet.canApprove || row.timesheet.canEdit) && row.timesheet.active_clock) {
                      <timesheet-row-clocked-in
                        [timesheet]="row.timesheet"
                        [schedule]="row.schedule"
                        [departments]="options?.departmentsById"
                        [locations]="options?.locationsById"
                        [departmentOptions]="
                          row.timesheet.user_id == options?.authenticatedUserId
                            ? options?.ownDepartmentOptions
                            : options?.departmentOptions
                        "
                        [teams]="options?.teams"
                        [shifts]="options?.shifts"
                        [rateCards]="options?.rateCards"
                        [absenceWarning]="row.absenceWarning"
                        [doubleRegistration]="row.doubleRegistration"
                        [deviationsFromSchedule]="row.deviationsFromSchedule"
                        [cols]="columns"
                        [customFields]="customFields"
                        [customFieldCols]="options.customFields"
                      >
                      </timesheet-row-clocked-in>
                    }
                    @if ((row.timesheet.canApprove || row.timesheet.canEdit) && !row.timesheet.active_clock) {
                      <timesheet-row-edit
                        [timesheet]="row.timesheet"
                        [schedule]="row.schedule"
                        [departments]="options?.departmentsById"
                        [locations]="options?.locationsById"
                        [departmentOptions]="
                          row.timesheet.user_id == options?.authenticatedUserId
                            ? options?.ownDepartmentOptions
                            : options?.departmentOptions
                        "
                        [teams]="options?.teams"
                        [shifts]="options?.shifts"
                        [rateCards]="options?.rateCards"
                        [absenceWarning]="row.absenceWarning"
                        [doubleRegistration]="row.doubleRegistration"
                        [deviationsFromSchedule]="row.deviationsFromSchedule"
                        [cols]="columns"
                        [customFields]="customFields"
                        [customFieldCols]="options.customFields"
                      >
                      </timesheet-row-edit>
                    }
                  </timesheet-content-row>
                  <timesheet-content-row
                    sbGridStickyColumn="right"
                    [sbGridStickyColumnAffordance]="false"
                    class="timesheet-totals-cell"
                  >
                    @if (!row.timesheet.canEdit && !row.timesheet.canApprove) {
                      <timesheet-total-timesheet
                        [timesheet]="row.timesheet"
                        [time]="row.timesheet.total"
                        [salary]="row.timesheet.salary"
                        [showSalary]="viewData.canViewSalary"
                        [coc]="row.timesheet.coc"
                        [showCoc]="showCoc"
                      >
                      </timesheet-total-timesheet>
                    }
                    @if ((row.timesheet.canApprove || row.timesheet.canEdit) && row.timesheet.active_clock) {
                      <timesheet-total-timesheet-clocked-in
                        [timesheet]="row.timesheet"
                        [time]="row.timesheet.total"
                        [salary]="row.timesheet.salary"
                        [showSalary]="viewData.canViewSalary"
                        [coc]="row.timesheet.coc"
                        [showCoc]="showCoc"
                      >
                      </timesheet-total-timesheet-clocked-in>
                    }
                    @if ((row.timesheet.canApprove || row.timesheet.canEdit) && !row.timesheet.active_clock) {
                      <timesheet-total-timesheet-edit
                        [timesheet]="row.timesheet"
                        [time]="row.timesheet.total"
                        [salary]="row.timesheet.salary"
                        [showSalary]="viewData.canViewSalary"
                        [coc]="row.timesheet.coc"
                        [showCoc]="showCoc"
                      >
                      </timesheet-total-timesheet-edit>
                    }
                  </timesheet-content-row>
                }
                @case ('schedule') {
                  <timesheet-content-row class="timesheet-centre-columns">
                    <timesheet-row-schedule
                      [schedule]="row.schedule"
                      [locations]="options?.locationsById"
                      [department]="(options?.departmentsById)[row.schedule.department_id]"
                      [cols]="columns"
                      [customFields]="customFields"
                      [customFieldCols]="options.customFields"
                    >
                    </timesheet-row-schedule>
                  </timesheet-content-row>
                  <timesheet-total-schedule
                    sbGridStickyColumn="right"
                    [sbGridStickyColumnAffordance]="false"
                    class="timesheet-totals-cell"
                    [dayIsClosed]="
                      openCloseData &&
                      openCloseData[day.day] &&
                      openCloseData[day.day][employeeId] &&
                      openCloseData[day.day][employeeId].availableDepartments &&
                      openCloseData[day.day][employeeId].availableDepartments.length === 0
                    "
                    [schedule]="row.schedule"
                    [employeeId]="employeeId"
                  >
                  </timesheet-total-schedule>
                }
                @case ('absentee') {
                  <timesheet-content-row class="timesheet-centre-columns">
                    <timesheet-row-absence
                      [absence]="row.absentee"
                      [date]="day.day"
                      [cols]="columns"
                      [customFields]="customFields"
                      [customFieldCols]="options.customFields"
                    >
                    </timesheet-row-absence>
                  </timesheet-content-row>
                  <timesheet-total-absence
                    sbGridStickyColumn="right"
                    [sbGridStickyColumnAffordance]="false"
                    class="timesheet-totals-cell"
                    [absence]="row.absentee"
                    [showSalary]="viewData.canViewSalary"
                    [showCoc]="showCoc"
                  >
                    <div absence-tooltip>
                      {{ 'Total absentee hours' | translate }}: {{ row.absentee?.hours | localeDuration: 'digital' }}
                      <br />
                      {{ 'Total wait hours' | translate }}:
                      {{ row.absentee?.wait_hours | localeDuration: 'digital' }}
                    </div>
                  </timesheet-total-absence>
                }
              }
            </div>
          }
        }
      </div>
      <!--
        Pulled up over the body's last pixel, so its rule covers the last row's own
        bottom rule rather than doubling it. Raised above the pinned cells, which
        would otherwise paint over it.
      -->
      <div
        class="relative z-sticky -mt-px shrink-0 overflow-x-hidden border-t border-default bg-surface"
        [sbGridScrollFollower]="gridBody"
      >
        <div sbGridScrollRow class="timesheet-row timesheet-day-total border-t-0">
          <timesheet-day-header
            sbGridStickyColumn
            [sbGridStickyColumnAffordance]="false"
            class="timesheet-info-cell"
            [title]="'Totals' | translate"
            [showAsFooter]="true"
          >
          </timesheet-day-header>
          <timesheet-content-row class="timesheet-centre-columns">
            <timesheet-day-blank-columns
              [cols]="columns"
              [customFields]="customFields"
              [customFieldCols]="options?.customFields"
            />
          </timesheet-content-row>
          <timesheet-grand-total
            sbGridStickyColumn="right"
            [sbGridStickyColumnAffordance]="false"
            class="timesheet-totals-cell"
            [time]="viewData?.approvedTotal.hours"
            [salary]="viewData?.approvedTotal.pay"
            [showSalary]="viewData?.canViewSalary"
            [showAsFooter]="true"
            [coc]="viewData?.approvedTotal.coc"
            [showCoc]="showCoc"
          >
          </timesheet-grand-total>
        </div>
      </div>
    </div>
  }
</div>
